import * as React from "react";
import { 
    gql, 
    useMutation, 
    ApolloError 
} from "@apollo/client";
import { MarkCampaignMutationMutation, MarkCampaignMutationMutationVariables } from "../../../../../schemaTypes";

export const markCampaignMutation = gql`
  mutation MarkCampaignMutation(
    $id: String!
    $option: String
    $close: Boolean
  ) {
    markCampaign(
      id: $id,
      option: $option,
      close: $close
    ) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: MarkCampaignMutationMutationVariables
      ) => Promise<{
        errors: ({ path: string; message: string } | null)[] | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

export const MarkCampaignController: React.FC<Props> = ({ children, onMessage }) => {
  const [markCampaign, { loading, error }] = useMutation<
    MarkCampaignMutationMutation,
    MarkCampaignMutationMutationVariables
  >(markCampaignMutation);

  const normalizeText = (value?: string | null) => {
    if (typeof value !== "string") {
      return undefined;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length ? trimmedValue : undefined;
  };

  const submit = async (
    values: MarkCampaignMutationMutationVariables
  ) => {
    const id = normalizeText(values.id);
    const option = normalizeText(values.option);

    if (!id) {
      return {
        errors: [{ path: "id", message: "Campaign id is required" }],
        message: undefined,
      };
    }

    const { data } = await markCampaign({
      variables: {
        id,
        option,
        close: values.close,
      },
    });

    const errors = data?.markCampaign?.errors ?? null;
    const message = data?.markCampaign?.message ?? undefined;

    if (message && onMessage) {
      onMessage(message);
    }

    return {
      errors,
      message,
    };
  };

  return children({ submit, loading, error });
};